/**
 * Baseline provenance - SIL-1831. Shape + validation for
 * `fmv_pricing_snapshot.baseline_provenance` (db/schema/1067).
 *
 * The snapshot's `baseline_price_usd` is the EFFECTIVE value fed to the
 * formula. When that value was DERIVED from the `sku_mcmp` row behind it (today:
 * the guarded "no_new_band_uplift" of a `new` server asset priced from a
 * like_new / normal_enterprise_wear baseline), this envelope records the donor
 * band, what was applied and the raw pre-conversion price, so the row is
 * reproducible and affected assets can be found and re-priced later.
 *
 * One versioned envelope on purpose (the table is append-only; a wrong shape
 * cannot be repaired): SIL-1832's `band_mode` and SIL-1833's `calibration`
 * have slots here and need no migration. This module is the ONLY place the
 * shape and reason codes are defined; the DB only guards the envelope (object
 * with a `v` key, sku_mcmp only, factor > 0).
 *
 *   {"v":1,"source_band":"like_new",
 *    "conversion":{"factor":1.1,"reason":"no_new_band_uplift","raw_price_usd":1234.56},
 *    "band_mode":null,"calibration":null}
 */

import { z } from 'zod';

/** Reason codes a `conversion` may carry. Extend here (and only here). */
export const BASELINE_CONVERSION_REASONS = ['no_new_band_uplift'] as const;
export type BaselineConversionReason = (typeof BASELINE_CONVERSION_REASONS)[number];

export const BASELINE_PROVENANCE_VERSION = 1 as const;

export const baselineConversionSchema = z.object({
  factor: z.number().positive().finite(),
  reason: z.enum(BASELINE_CONVERSION_REASONS),
  /** The sku_mcmp median BEFORE `factor` was applied. */
  raw_price_usd: z.number().nonnegative().finite(),
});

export const baselineProvenanceSchema = z.object({
  v: z.literal(BASELINE_PROVENANCE_VERSION),
  /**
   * The sku_mcmp row's condition band the baseline came from. A free string,
   * NOT the condition_band enum: the enum grows (621) and old rows must stay
   * readable.
   */
  source_band: z.string().min(1),
  conversion: baselineConversionSchema.nullable(),
  /** Reserved for SIL-1832 (band_aware mode). Always null today. */
  band_mode: z.record(z.unknown()).nullable(),
  /** Reserved for SIL-1833 (ask-to-executed calibration). Always null today. */
  calibration: z.record(z.unknown()).nullable(),
});

export type BaselineProvenance = z.infer<typeof baselineProvenanceSchema>;

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Provenance for a baseline scaled by a guarded fixed uplift. */
export function buildUpliftProvenance(args: {
  sourceBand: string;
  factor: number;
  rawPriceUsd: number;
}): BaselineProvenance {
  return baselineProvenanceSchema.parse({
    v: BASELINE_PROVENANCE_VERSION,
    source_band: args.sourceBand,
    conversion: {
      factor: args.factor,
      reason: 'no_new_band_uplift',
      raw_price_usd: round2(args.rawPriceUsd),
    },
    band_mode: null,
    calibration: null,
  });
}

/**
 * Validates a provenance value read from a snapshot row (carry-forward) or
 * handed to the writer. null/undefined -> null. A value that does not match
 * the envelope THROWS: silently dropping provenance would leave an uplifted
 * baseline with no explanation, which is the failure this column exists to
 * prevent.
 */
export function parseBaselineProvenance(raw: unknown): BaselineProvenance | null {
  if (raw === null || raw === undefined) return null;
  const parsed = baselineProvenanceSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`invalid baseline_provenance: ${parsed.error.message}`);
  }
  return parsed.data;
}

/**
 * The provenance a snapshot write should persist. Provenance describes how a
 * `sku_mcmp` row became the baseline, so it is only kept for that source; any
 * other source (manual override, oem_list re-resolution) CLEARS it. This is
 * the single clear/keep rule every write path goes through
 * (writeFmvSnapshot), so a carry-forward cannot attach a stale conversion to
 * a baseline that was since replaced.
 */
export function effectiveBaselineProvenance(
  baselinePriceSourceId: string,
  raw: unknown,
): BaselineProvenance | null {
  if (baselinePriceSourceId !== 'sku_mcmp') return null;
  return parseBaselineProvenance(raw);
}

// ---------------------------------------------------------------------------
// SIL-1955: replacement-cost provenance variant. DEFINED, NOT YET USED: phase 2
// (SIL-1954) decides whether replacement cost is a baseline or a cap, and will
// record the exact sku_replacement_cost row on the snapshot with this envelope.
// It is deliberately NOT part of `baselineProvenanceSchema` / `parseBaselineProvenance`
// (the sku_mcmp envelope above) so nothing that reads existing snapshots changes.
//
//   {"v":1,"kind":"replacement_cost","replacement_cost_id":"<uuid>","method":"oem_configurator",
//    "price_low_usd":86000,"price_high_usd":90000,"observed_at":"2026-10-14T00:00:00.000Z"}
// ---------------------------------------------------------------------------

export const replacementCostProvenanceSchema = z.object({
  v: z.literal(BASELINE_PROVENANCE_VERSION),
  kind: z.literal('replacement_cost'),
  replacement_cost_id: z.string().uuid(),
  method: z.enum(['oem_configurator', 'bom', 'manual']),
  price_low_usd: z.number().positive().finite(),
  price_high_usd: z.number().positive().finite(),
  observed_at: z.string().datetime(),
});

export type ReplacementCostProvenance = z.infer<typeof replacementCostProvenanceSchema>;

/** Builds (and validates) the replacement-cost provenance envelope for a sku_replacement_cost row. */
export function buildReplacementCostProvenance(args: {
  replacementCostId: string;
  method: ReplacementCostProvenance['method'];
  priceLowUsd: number;
  priceHighUsd: number;
  observedAt: string;
}): ReplacementCostProvenance {
  return replacementCostProvenanceSchema.parse({
    v: BASELINE_PROVENANCE_VERSION,
    kind: 'replacement_cost',
    replacement_cost_id: args.replacementCostId,
    method: args.method,
    price_low_usd: round2(args.priceLowUsd),
    price_high_usd: round2(args.priceHighUsd),
    observed_at: args.observedAt,
  });
}
